/**
 * Página Favoritos - Guías y plantillas marcadas como favoritas
 * Mismo diseño que el Dashboard (Topbar + Sidebar + contenido)
 * Ruta: /favoritos
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, Typography, Button } from "@mui/material";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ExploreIcon from "@mui/icons-material/Explore";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout, ExploreSection } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import { PurchaseChoiceModal } from "../components/PurchaseChoiceModal";
import ComingSoonModal from "../components/ComingSoonModal";
import { trackGuideInteraction, trackPurchaseAction } from "../utils/analytics";

import "../components/Dashboard/Dashboard.css";
import { GUIDE_CATALOG_IMAGES } from "../data/guideCatalogImages";
import plantillaTodo from "../assets/to-do.png";
import travelkitCover from "../assets/travel-kit2.png";

const imageMap = {
	...GUIDE_CATALOG_IMAGES,
	"todo-viaje": plantillaTodo,
	travelkit: travelkitCover,
};

const FavoritosPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		user,
		handleLogout: contextLogout,
		favorites,
		getPrebuiltGuides,
		checkGuideAccess,
		toggleFavorite,
		subscription,
		importPrebuiltGuide,
		canCreateGuide,
		purchaseGuide,
		purchaseSubscription,
	} = useFirebase();

	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");
	const [favoriteGuides, setFavoriteGuides] = useState([]);
	const [loadingGuides, setLoadingGuides] = useState(true);

	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home") {
			navigate("/");
			return;
		}
		if (sectionId === "explore") {
			navigate("/explorar");
			return;
		}
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "favorites") return;
		if (sectionId === "create") {
			navigate("/crear-guia");
			return;
		}
		if (sectionId === "recent") {
			navigate("/");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		// logros-viajeros comentado
		// if (sectionId === "logros-viajeros") {
		// 	if (!user) {
		// 		setAuthMode("login");
		// 		setShowLogin(true);
		// 		enqueueSnackbar("Inicia sesión para ver tus logros viajeros", { variant: "info" });
		// 		return;
		// 	}
		// 	navigate("/logros-viajeros");
		// 	return;
		// }
		if (sectionId === "modules") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus módulos", { variant: "info" });
				return;
			}
			navigate("/modulos");
			return;
		}
		if (sectionId === "discounts") {
			navigate("/descuentos");
			return;
		}
		if (sectionId === "help") {
			navigate("/", { state: { section: "help" } });
			return;
		}
		if (sectionId === "settings") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para acceder a la configuración", {
					variant: "info",
				});
				return;
			}
			navigate("/settings");
			return;
		}
	};

	const handleCreateFromScratch = () => {
		navigate("/crear-guia");
	};

	const handleLogout = async () => {
		await contextLogout();
		enqueueSnackbar("Sesión cerrada", { variant: "success" });
	};

	// Cargar guías favoritas
	useEffect(() => {
		const loadFavoriteGuides = async () => {
			if (!user || !favorites?.length) {
				setFavoriteGuides([]);
				setLoadingGuides(false);
				return;
			}
			setLoadingGuides(true);
			try {
				const allGuides = await getPrebuiltGuides();
				const withImages = (allGuides || [])
					.filter((g) => favorites.includes(g.id))
					.map((guide) => {
						const access = checkGuideAccess(guide.id, guide);
						return {
							...guide,
							coverImage: imageMap[guide.id] || guide.coverImage,
							days: guide.days || null,
							tags: guide.tags || [],
							isFree: guide.isFree,
							price: guide.price,
							userHasAccess: guide.userHasAccess ?? access.hasFullAccess,
						};
					});
				setFavoriteGuides(withImages);
			} catch (error) {
				console.error("Error cargando favoritos:", error);
				setFavoriteGuides([]);
			} finally {
				setLoadingGuides(false);
			}
		};
		loadFavoriteGuides();
		// Cargar al montar o al cambiar usuario/suscripción; no por referencias del contexto.
		// Al quitar un favorito se actualiza la lista en handleToggleFavorite (optimista).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, subscription]);

	const [purchasingGuide, setPurchasingGuide] = useState(null);
	const [purchaseChoiceModal, setPurchaseChoiceModal] = useState({
		open: false,
		guide: null,
		limitInfo: null,
	});
	const [comingSoonModal, setComingSoonModal] = useState({
		open: false,
		guideName: null,
	});

	const handleViewGuide = (guideId) => {
		trackGuideInteraction("preview_clicked", guideId, { source: "favoritos" });
		navigate(`/preview/prebuilt/${guideId}`, {
			state: { from: `${location.pathname}${location.search}` },
		});
	};

	const handleCustomizeGuide = async (guideId) => {
		trackGuideInteraction("customize_clicked", guideId, { source: "favoritos" });
		const guide = favoriteGuides.find((g) => g.id === guideId);
		if (guide && !guide.isFree && !guide.userHasAccess) {
			enqueueSnackbar(
				`Esta guía requiere compra o suscripción (${
					guide.price ? `€${guide.price}` : "Premium"
				})`,
				{ variant: "warning" },
			);
			return;
		}
		const createCheck = canCreateGuide();
		if (!createCheck.allowed) {
			setPurchaseChoiceModal({
				open: true,
				guide,
				limitInfo: {
					currentGuides: createCheck.currentGuides,
					maxGuides: createCheck.maxGuides,
				},
			});
			return;
		}
		try {
			enqueueSnackbar("Importando guía...", { variant: "info" });
			const result = await importPrebuiltGuide(guideId);
			if (result?.project) {
				enqueueSnackbar("Guía importada correctamente", { variant: "success" });
				const userId = result.project.userId || user.uid || user.id;
				navigate(`/usuarios/${userId}/guias/${result.project.id}`);
			} else {
				throw new Error("No se pudo crear la guía");
			}
		} catch (error) {
			console.error("Error importing guide:", error);
			if (
				error.code === "GUIDE_LIMIT" ||
				error.message?.includes("límite de guías")
			) {
				setPurchaseChoiceModal({
					open: true,
					guide,
					limitInfo: {
						currentGuides: createCheck.currentGuides,
						maxGuides: createCheck.maxGuides,
					},
				});
			} else {
				enqueueSnackbar(error.message || "No se pudo crear la guía", {
					variant: "error",
				});
			}
		}
	};

	const handlePurchaseClick = (guide) => {
		trackPurchaseAction("modal_opened", {
			guide_id: guide?.id ? String(guide.id) : undefined,
			guide_name: guide?.name,
			source: "favoritos",
		});
		setPurchaseChoiceModal({ open: true, guide });
	};

	const handleConfirmBuyGuide = async (guide) => {
		if (!guide?.id) return;
		try {
			await purchaseGuide(guide.id);
		} catch (error) {
			enqueueSnackbar(error.message || "Error al procesar la compra", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		}
	};

	const handleConfirmSubscribe = async () => {
		try {
			await purchaseSubscription();
		} catch (error) {
			enqueueSnackbar(error.message || "Error al iniciar la suscripción", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		}
	};

	const handleToggleFavorite = async (guideId) => {
		const wasFavorite = favorites?.includes(guideId);
		try {
			await toggleFavorite(guideId);
			// Quitar de la lista al instante (optimista) para no mostrar loading ni recarga
			if (wasFavorite) {
				setFavoriteGuides((prev) => prev.filter((g) => g.id !== guideId));
			}
			enqueueSnackbar(
				wasFavorite ? "Quitado de favoritos" : "Añadido a favoritos",
				{ variant: "success" },
			);
		} catch (error) {
			enqueueSnackbar("Error al actualizar favoritos", { variant: "error" });
		}
	};

	return (
		<>
			<DashboardLayout
				user={formattedUser}
				activeSection="favorites"
				onSectionChange={handleSectionChange}
				onLogin={() => {
					setAuthMode("login");
					setShowLogin(true);
				}}
				onSignUp={() => {
					setAuthMode("signup");
					setShowLogin(true);
				}}
				onLogout={handleLogout}
				onCreateNew={handleCreateFromScratch}
				onSearch={() => {}}
				prebuiltGuides={favoriteGuides || []}
				imageMap={imageMap}
				onComingSoonGuide={(g) =>
					setComingSoonModal({ open: true, guideName: g?.name ?? null })
				}
			>
				{!user ? (
					<Box
						sx={{
							textAlign: "center",
							py: 8,
							px: 3,
							backgroundColor: "#fff",
							borderRadius: "24px",
							border: "2px dashed rgba(99, 102, 241, 0.2)",
							maxWidth: 480,
							mx: "auto",
						}}
					>
						<Box
							sx={{
								width: 80,
								height: 80,
								borderRadius: "50%",
								backgroundColor: "rgba(99, 102, 241, 0.1)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mx: "auto",
								mb: 2,
							}}
						>
							<PersonOutlineIcon sx={{ fontSize: 40, color: "#6366F1" }} />
						</Box>
						<Typography
							variant="h6"
							sx={{ color: "#1a1a2e", fontWeight: 600, mb: 1 }}
						>
							Favoritos
						</Typography>
						<Typography sx={{ color: "#64748B", fontSize: "0.938rem", mb: 3 }}>
							Inicia sesión para guardar plantillas y guías como favoritas
						</Typography>
						<Button
							variant="contained"
							onClick={() => {
								setAuthMode("login");
								setShowLogin(true);
							}}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								fontSize: "0.938rem",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								borderRadius: "12px",
								px: 3,
								py: 1.25,
								boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
								},
							}}
						>
							Iniciar sesión
						</Button>
					</Box>
				) : favoriteGuides.length > 0 ? (
					<ExploreSection
						guides={favoriteGuides}
						loading={loadingGuides}
						onViewGuide={handleViewGuide}
						onCustomizeGuide={handleCustomizeGuide}
						onPurchaseGuide={handlePurchaseClick}
						onComingSoonClick={(g) =>
							setComingSoonModal({ open: true, guideName: g?.name ?? null })
						}
						purchasingGuideId={purchasingGuide}
						onSeeAll={() => navigate("/explorar")}
						user={formattedUser}
						favoriteIds={favorites || []}
						onToggleFavorite={handleToggleFavorite}
						title="Favoritos"
						subtitle="Tus guías y plantillas guardadas"
						hideTabs
						hideSeeAllButton
					/>
				) : (
					<Box
						sx={{
							textAlign: "center",
							py: 10,
							px: 3,
							backgroundColor: "#fff",
							borderRadius: "24px",
							border: "2px dashed rgba(0, 0, 0, 0.08)",
							maxWidth: 520,
							mx: "auto",
						}}
					>
						<Box
							sx={{
								width: 80,
								height: 80,
								borderRadius: "50%",
								backgroundColor: "rgba(251, 191, 36, 0.15)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								mx: "auto",
								mb: 2,
							}}
						>
							<StarBorderIcon sx={{ fontSize: 40, color: "#F59E0B" }} />
						</Box>
						<Typography
							variant="h5"
							sx={{
								fontWeight: 700,
								color: "#1a1a2e",
								mb: 1,
								fontSize: { xs: "1.25rem", md: "1.5rem" },
							}}
						>
							Favoritos
						</Typography>
						<Typography sx={{ color: "#64748B", fontSize: "0.938rem", mb: 3 }}>
							Las guías y plantillas que marques como favoritas aparecerán aquí.
							Explora guías y usa el icono de corazón para guardarlas.
						</Typography>
						<Button
							variant="contained"
							onClick={() => navigate("/explorar")}
							startIcon={<ExploreIcon />}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								fontSize: "0.938rem",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								borderRadius: "12px",
								px: 3,
								py: 1.25,
								boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
								},
							}}
						>
							Explorar plantillas
						</Button>
					</Box>
				)}
			</DashboardLayout>

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>

			<PurchaseChoiceModal
				open={purchaseChoiceModal.open}
				guide={purchaseChoiceModal.guide}
				onClose={() =>
					setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null })
				}
				onBuyGuide={handleConfirmBuyGuide}
				onSubscribe={handleConfirmSubscribe}
				loading={!!purchasingGuide}
				limitInfo={purchaseChoiceModal.limitInfo}
			/>

			<ComingSoonModal
				open={comingSoonModal.open}
				onClose={() => setComingSoonModal({ open: false, guideName: null })}
				guideName={comingSoonModal.guideName}
			/>
		</>
	);
};

export default FavoritosPage;
